import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useHasMenu } from '../../../auth/access';
import { ConfirmationModal } from '../../shared/components/feedback';
import { bal, errorText, Group, groupsApi, groupTree, Ledger, ledgersApi } from '../accounting.api';
import BooksHeader from '../components/BooksHeader';
import { useBooksCompany } from '../components/CompanySelect';
import LedgerDialog from '../components/LedgerDialog';

/** Ledgers with today's balance; add / edit / delete for those allowed. A name opens its Ledger Book. */
export default function LedgersPage() {
  const { companies, companyId, setCompanyId, loaded } = useBooksCompany();
  const canEdit = useHasMenu('accounts.ledgers', 'EDIT');
  const canBook = useHasMenu('accounts.ledger-book');

  const [ledgers, setLedgers] = useState<Ledger[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [groupId, setGroupId] = useState<number | ''>('');
  const [editing, setEditing] = useState<Ledger | null | 'new'>(null);
  const [deleting, setDeleting] = useState<Ledger | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    Promise.all([ledgersApi.list(companyId), groupsApi.list(companyId)])
      .then(([l, g]) => {
        setLedgers(l);
        setGroups(g);
      })
      .catch((e) => setError(errorText(e, 'Could not load ledgers', 'ACCOUNTING_LEDGERS_FAILED')))
      .finally(() => setLoading(false));
  }, [companyId]);

  useEffect(() => {
    setGroupId('');
    load();
  }, [load]);

  const tree = useMemo(() => groupTree(groups), [groups]);

  // A group filter includes everything under it.
  const inGroup = useMemo(() => {
    if (groupId === '') return null;
    const ids = new Set<number>([groupId]);
    tree.forEach(({ group }) => {
      if (group.parentId && ids.has(group.parentId)) ids.add(group.id);
    });
    return ids;
  }, [groupId, tree]);

  const shown = ledgers.filter((l) => {
    if (inGroup && !inGroup.has(l.groupId)) return false;
    const s = q.trim().toLowerCase();
    return !s || l.name.toLowerCase().includes(s) || (l.code ?? '').toLowerCase().includes(s) || l.groupName.toLowerCase().includes(s);
  });

  const remove = async () => {
    if (!companyId || !deleting) return;
    setBusy(true);
    try {
      await ledgersApi.remove(companyId, deleting.id);
      toast.success(`${deleting.name} deleted.`);
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(errorText(e, 'Could not delete the ledger', 'ACCOUNTING_LEDGER_DELETE_FAILED'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap={3}>
      <BooksHeader
        title='LEDGERS'
        companies={companies}
        companyId={companyId}
        onCompany={setCompanyId}
        loaded={loaded}
        action={
          canEdit && (
            <Button variant='contained' startIcon={<Icon icon='mdi:plus' />} disabled={!companyId || groups.length === 0} onClick={() => setEditing('new')}>
              New ledger
            </Button>
          )
        }
      >
        <TextField
          size='small'
          placeholder='Search name, code, group'
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: 240, flex: { xs: 1, md: 'none' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Icon icon='mdi:magnify' />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size='small'
          label='Group'
          value={groupId}
          onChange={(e) => setGroupId(e.target.value === '' ? '' : Number(e.target.value))}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value=''>All groups</MenuItem>
          {tree.map(({ group, depth }) => (
            <MenuItem key={group.id} value={group.id} sx={{ pl: 2 + depth * 2, fontWeight: depth === 0 ? 700 : 400 }}>
              {group.name}
            </MenuItem>
          ))}
        </TextField>
      </BooksHeader>

      {loading && <LinearProgress />}
      {error && <Alert severity='error'>{error}</Alert>}

      {companyId && (
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Ledger</TableCell>
                <TableCell>Group</TableCell>
                <TableCell align='right'>Current balance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align='right' />
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && shown.length === 0 && (
                <TableRow>
                  <TableCell {...({ colSpan: 5 } as object)}>
                    <Typography variant='body2' color='text.secondary'>
                      No ledgers match.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {shown.map((l) => (
                <TableRow key={l.id} hover>
                  <TableCell sx={{ minWidth: 180 }}>
                    {canBook ? (
                      <Link component={RouterLink} to={`/accounts/ledger-book?ledgerId=${l.id}`} underline='hover' fontWeight={600} variant='body2'>
                        {l.name}
                      </Link>
                    ) : (
                      <Typography variant='body2' fontWeight={600}>
                        {l.name}
                      </Typography>
                    )}
                    {l.code && (
                      <Typography variant='caption' color='text.secondary' display='block'>
                        {l.code}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2'>{l.groupName}</Typography>
                    {l.cashBank && <Chip size='small' variant='outlined' label={l.cashBank === 'CASH' ? 'Cash' : 'Bank'} sx={{ mt: 0.25 }} />}
                  </TableCell>
                  <TableCell align='right' sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                    {bal(l.currentBalance)}
                  </TableCell>
                  <TableCell>
                    <Stack direction='row' gap={0.5}>
                      {l.active ? <Chip size='small' color='success' variant='outlined' label='Active' /> : <Chip size='small' label='Inactive' />}
                      {l.system && <Chip size='small' variant='outlined' label='System' />}
                    </Stack>
                  </TableCell>
                  <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                    {canBook && (
                      <Tooltip title='Ledger book'>
                        <IconButton size='small' component={RouterLink} to={`/accounts/ledger-book?ledgerId=${l.id}`}>
                          <Icon icon='mdi:book-open-page-variant-outline' />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canEdit && (
                      <Tooltip title='Edit'>
                        <IconButton size='small' onClick={() => setEditing(l)}>
                          <Icon icon='mdi:pencil' />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canEdit && !l.system && (
                      <Tooltip title='Delete'>
                        <IconButton size='small' color='error' onClick={() => setDeleting(l)}>
                          <Icon icon='mdi:delete-outline' />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <LedgerDialog
        open={editing !== null}
        companyId={companyId}
        ledger={editing === 'new' ? null : editing}
        groups={groups}
        onClose={() => setEditing(null)}
        onSaved={load}
      />

      <ConfirmationModal
        open={deleting !== null}
        title={`Delete ${deleting?.name ?? ''}?`}
        message='A ledger that has vouchers cannot be deleted; deactivate it instead.'
        confirmText='Delete'
        onConfirm={remove}
        onClose={() => setDeleting(null)}
        loading={busy}
      />
    </Stack>
  );
}
